import React, { useEffect, useState } from "react";
import { growthApi } from "../api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Target, Flame, ListTodo, Users, BellRing, Mail } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const TAG_COLORS = {
  "URGENT HOT LEAD": "bg-red-100 text-red-700 border-red-200",
  "CONSULTATION / INTERVIEW": "bg-blue-100 text-blue-700 border-blue-200",
  "INTAKE / AUTHORIZATION": "bg-violet-100 text-violet-700 border-violet-200",
  "STAFFING GAP": "bg-amber-100 text-amber-700 border-amber-200",
  "WARM FOLLOW-UP": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "SEO / RESEARCH": "bg-slate-100 text-slate-600 border-slate-200",
};

const StatChip = ({ label, value, accent = "#0E5A72" }) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3 text-center">
    <div className="font-display font-extrabold text-2xl" style={{ color: accent }}>{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);

const AcquisitionPanel = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState("");

  const runReminders = async () => {
    setBusy("rem");
    try {
      const r = await growthApi.runReminders();
      const n = r.data.remindersSentFor;
      toast({ title: n ? `Reminder email sent (${n} overdue lead${n > 1 ? "s" : ""})` : "All caught up!", description: n ? "Check info@myohcare.com." : "No leads waiting more than 24 hours without contact." });
    } catch {
      toast({ title: "Could not run reminders", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const sendDigest = async () => {
    setBusy("dig");
    try {
      await growthApi.sendDigest();
      toast({ title: "Weekly digest sent", description: "Check info@myohcare.com. It also sends automatically every Monday morning." });
    } catch {
      toast({ title: "Could not send digest", variant: "destructive" });
    } finally { setBusy(""); }
  };

  useEffect(() => {
    growthApi.acquisition().then((r) => setData(r.data)).catch(() => {});
    growthApi.actions().then((r) => setActions(r.data.actions)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center text-slate-400 py-10">Loading acquisition data…</div>;

  const stage = (s) => data.byStage[s] || 0;
  const pct = Math.min(100, (data.placements / data.placementGoal) * 100);

  return (
    <div data-testid="acquisition-panel" className="space-y-6">
      <div className="flex flex-wrap gap-3 justify-end">
        <Button data-testid="run-reminders-btn" size="sm" variant="outline" disabled={busy === "rem"} onClick={runReminders} className="rounded-full border-[#0E5A72] text-[#0E5A72]">
          <BellRing className="mr-2 h-4 w-4" /> {busy === "rem" ? "Checking…" : "Run 24h follow-up check"}
        </Button>
        <Button data-testid="send-digest-btn" size="sm" variant="outline" disabled={busy === "dig"} onClick={sendDigest} className="rounded-full border-[#2E9E6A] text-[#23875A]">
          <Mail className="mr-2 h-4 w-4" /> {busy === "dig" ? "Sending…" : "Email me the digest now"}
        </Button>
      </div>
      {/* Goal tracker */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2">
            <Target className="h-5 w-5 text-[#2E9E6A]" /> Progress toward 3 qualified client placements / commitments by September 30, 2026
          </h3>
          <span className="font-display font-extrabold text-2xl text-[#0E5A72]">{data.placements} / {data.placementGoal}</span>
        </div>
        <div className="h-4 rounded-full bg-slate-100 overflow-hidden mt-3">
          <div className="h-full rounded-full bg-[#2E9E6A] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          A placement counts only at "Ready to Start" or "Won" — an email reply is never automatically a placement.
          Verify eligibility, authorization and program participation before services begin.
        </p>
      </div>

      {/* Stat chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatChip label="Total Leads" value={data.total} />
        <StatChip label="HOT" value={data.byTemperature.HOT || 0} accent="#DC2626" />
        <StatChip label="WARM" value={data.byTemperature.WARM || 0} accent="#D97706" />
        <StatChip label="Consultations" value={stage("Consultation Scheduled")} accent="#2563EB" />
        <StatChip label="Intakes" value={stage("Intake Started")} accent="#7C3AED" />
        <StatChip label="Ready / Won" value={stage("Ready to Start") + stage("Won")} accent="#2E9E6A" />
        <StatChip label="Staffing Gaps" value={data.staffingGaps} accent="#DC2626" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily action center */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2 mb-1">
            <ListTodo className="h-5 w-5 text-[#0E5A72]" /> What should we do today?
          </h3>
          <p className="text-sm text-slate-500 mb-4">Real ready clients always come before cold SEO work.</p>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {actions.map((a, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 px-4 py-3">
                <span className="font-display font-bold text-slate-300 text-lg w-5">{i + 1}</span>
                <div className="min-w-0">
                  <Badge className={`border text-[10px] ${TAG_COLORS[a.tag] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{a.tag}</Badge>
                  <div className="font-medium text-[#0E2A33] text-sm mt-1">{a.label}</div>
                  <div className="text-xs text-slate-500">{a.detail}</div>
                </div>
              </div>
            ))}
            {actions.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No pending actions — new leads will appear here.</div>}
          </div>
        </div>

        <div className="space-y-6">
          {/* By referral type */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-[#0E5A72]" /> Leads by channel
            </h3>
            <div className="space-y-2">
              {Object.entries(data.byReferralType).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm rounded-lg border border-slate-100 px-3 py-2">
                  <span className="text-slate-700">{k}</span>
                  <span className="font-semibold text-[#0E5A72]">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pipeline stages */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2 mb-4">
              <Flame className="h-5 w-5 text-[#D97706]" /> Pipeline stages
            </h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.byStage).map(([k, v]) => (
                <Badge key={k} className="border bg-[#F7FBFC] text-slate-700 border-slate-200 px-3 py-1.5">{k}: {v}</Badge>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcquisitionPanel;
